import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Tooltip,
  listItemButtonClasses,
  listItemIconClasses,
} from '@mui/material';

import { useMainLayoutContext } from '../Context/useMainLayoutContext';
import { VerticalNavItem } from './types';

export type { VerticalNavItem };

interface Props {
  navigationItems: VerticalNavItem[];
  isLoading: boolean;
}

export const CollapsibleVerticalNav: FC<Props> = ({ navigationItems, isLoading }) => {
  const { t } = useTranslation();
  const { isDrawerCollapsed } = useMainLayoutContext();

  const handleItemClick = (item: VerticalNavItem) => (e: MouseEvent) => {
    if (item.isDivider || item.disabled) {
      e.preventDefault();
      return;
    }

    // alwaysClickable items (e.g. ContentHub) stay interactive even when selected,
    // so a user on a sub-tab can click the parent to return to the hub menu.
    if (item.selected && !item.alwaysClickable) {
      e.preventDefault();
      return;
    }

    item.onClick?.(e);
  };

  return (
    <Box component="nav" aria-label={t('navigationMenu')} sx={{ width: '100%', minWidth: 0 }}>
      {isLoading ? (
        <List disablePadding component="ul">
          {Array.from({ length: 5 }).map((_, index) => (
            <ListItem key={index} disableGutters>
              <Skeleton variant="rounded" width="100%" height={40} />
            </ListItem>
          ))}
        </List>
      ) : (
        <List
          disablePadding
          component="ul"
          sx={(theme) => ({
            display: 'flex',
            width: !isDrawerCollapsed ? '220px' : 'auto',
            padding: '24px 0',
            flexDirection: 'column',
            alignItems: !isDrawerCollapsed ? 'flex-start' : 'center',
            gap: 1,

            [`& .${listItemButtonClasses.root}`]: {
              borderRadius: theme.spacing(0.5),
            },

            [`& .${listItemIconClasses.root}`]: {
              color: theme.palette.secondary.main,
            },
          })}
        >
          {navigationItems.map((item) => {
            if (item.isDivider) {
              return (
                <Divider
                  key={item.value}
                  component="li"
                  sx={{ width: '100%', my: 1, listStyle: 'none' }}
                />
              );
            }

            return (
              <ListItem key={item.text || item.value} disablePadding sx={{ width: '100%' }}>
                {isDrawerCollapsed ? (
                  <Tooltip title={item.text || ''} placement="right" arrow>
                    <ListItemButton
                      {...(item.href ? { href: item.href } : {})}
                      color="secondary"
                      aria-current={item.selected ? 'page' : undefined}
                      selected={item.selected}
                      disabled={item.disabled}
                      sx={{ p: 1, width: '100%' }}
                      onClick={handleItemClick(item)}
                    >
                      <ListItemIcon sx={{ minWidth: 0 }}>
                        <Badge
                          badgeContent={item.badgeCount}
                          color="primary"
                          invisible={!item.badgeCount}
                        >
                          {item.IconComponent && <item.IconComponent />}
                        </Badge>
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                ) : (
                  <ListItemButton
                    {...(item.href ? { href: item.href } : {})}
                    color="secondary"
                    aria-current={item.selected ? 'page' : undefined}
                    selected={item.selected}
                    disabled={item.disabled}
                    sx={{ p: 1, width: '100%' }}
                    onClick={handleItemClick(item)}
                  >
                    <ListItemIcon sx={{ mr: 1, minWidth: 0 }}>
                      {item.IconComponent && <item.IconComponent />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          {item.text}
                          {item.badgeCount != null && item.badgeCount > 0 && (
                            <Badge
                              badgeContent={item.badgeCount}
                              color="primary"
                              sx={{
                                '& .MuiBadge-badge': {
                                  position: 'static',
                                  transform: 'none',
                                  ml: 1,
                                  border: '2px solid white',
                                },
                              }}
                            />
                          )}
                        </Box>
                      }
                      slotProps={{
                        primary: {
                          sx: (theme) => ({
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textTransform: 'uppercase',
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.body2.fontSize,
                            mt: 0.25,
                          }),
                        },
                      }}
                      sx={{ m: 0, flex: 1 }}
                    />
                  </ListItemButton>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};
